import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDateTime, t } from '@aquabox/shared';
import { Icon } from './Icon';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
} from '../api/queries';
import type { AppNotification } from '../api/types';

/** Hedef type → route eşlemesi. Bilinmeyen ya da null → undefined (gitmez). */
export function notificationHref(
  n: Pick<AppNotification, 'targetType' | 'targetId'>,
): string | undefined {
  if (n.targetType === null || n.targetId === null) return undefined;
  switch (n.targetType) {
    case 'order':
      return `/siparisler?order=${n.targetId}`;
    case 'trip':
      return `/seferler?trip=${n.targetId}`;
    case 'customer':
      return `/musteriler?customer=${n.targetId}`;
    case 'reload':
    case 'reloadRequest':
      return `/seferler`;
    case 'report':
      return `/raporlar`;
    case 'stock':
    case 'stockCount':
      return `/stok`;
    default:
      return undefined;
  }
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'unread' | 'all'>('unread');
  const { data } = useNotifications();
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const items = data?.items ?? [];
  const unreadCount = items.filter((n) => n.readAt === null).length;

  // Dışarı tıklama ve Escape ile kapat.
  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (e: PointerEvent) => {
      const panel = panelRef.current;
      const button = buttonRef.current;
      if (
        panel &&
        !panel.contains(e.target as Node) &&
        button &&
        !button.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  // Türetilmiş değer: okunmamış yoksa Tümü, varsa seçili sekme.
  const effectiveTab = unreadCount === 0 ? 'all' : tab;

  const displayedItems = effectiveTab === 'unread' ? items.filter((n) => n.readAt === null) : items;

  const handleRowClick = (n: AppNotification) => {
    if (n.readAt === null) markRead.mutate(n.id);
    const href = notificationHref(n);
    if (href) void navigate(href);
    setOpen(false);
  };

  const badgeLabel =
    unreadCount > 0
      ? t('panel.notificationsUnread', { count: unreadCount })
      : t('panel.notifications');

  return (
    <div style={{ position: 'relative' }}>
      <button
        ref={buttonRef}
        aria-label={badgeLabel}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        style={{
          width: 38,
          height: 38,
          borderRadius: 'var(--r-row)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          color: 'var(--text-secondary)',
        }}
      >
        <Icon name="bell" />
        {unreadCount > 0 && (
          <span
            aria-hidden
            style={{
              position: 'absolute',
              top: 4,
              right: 4,
              minWidth: 18,
              height: 18,
              padding: '0 4px',
              borderRadius: 999,
              background: 'var(--danger)',
              color: '#fff',
              fontSize: 10,
              fontWeight: 700,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 1,
            }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={panelRef}
          style={{
            position: 'absolute',
            right: 0,
            top: 44,
            width: 'min(360px, calc(100vw - 24px))',
            maxHeight: 'min(480px, 70vh)',
            overflowY: 'auto',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-card)',
            boxShadow: '0 10px 30px rgba(16,30,56,.14)',
            zIndex: 30,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '10px 14px',
              borderBottom: '1px solid var(--border)',
              gap: 8,
              flexShrink: 0,
            }}
          >
            <div style={{ display: 'flex', gap: 4 }}>
              <button
                onClick={() => setTab('unread')}
                aria-pressed={effectiveTab === 'unread'}
                style={{
                  padding: '4px 10px',
                  borderRadius: 'var(--r-pill)',
                  fontSize: 12.5,
                  fontWeight: 600,
                  background: effectiveTab === 'unread' ? 'var(--primary)' : 'transparent',
                  color: effectiveTab === 'unread' ? '#fff' : 'var(--text-secondary)',
                  border: '1px solid',
                  borderColor: effectiveTab === 'unread' ? 'var(--primary)' : 'var(--border)',
                }}
              >
                {t('panel.notifTabUnread')}
              </button>
              <button
                onClick={() => setTab('all')}
                aria-pressed={effectiveTab === 'all'}
                style={{
                  padding: '4px 10px',
                  borderRadius: 'var(--r-pill)',
                  fontSize: 12.5,
                  fontWeight: 600,
                  background: effectiveTab === 'all' ? 'var(--primary)' : 'transparent',
                  color: effectiveTab === 'all' ? '#fff' : 'var(--text-secondary)',
                  border: '1px solid',
                  borderColor: effectiveTab === 'all' ? 'var(--primary)' : 'var(--border)',
                }}
              >
                {t('panel.notifTabAll')}
              </button>
            </div>
            {unreadCount > 0 && (
              <button
                onClick={() => markAll.mutate()}
                disabled={markAll.isPending}
                style={{ color: 'var(--primary)', fontWeight: 600, fontSize: 12.5 }}
              >
                {t('boss.markAllRead')}
              </button>
            )}
          </div>

          {displayedItems.length === 0 && (
            <p style={{ padding: 14, fontSize: 13, color: 'var(--text-secondary)' }}>
              {t('boss.noNotifications')}
            </p>
          )}

          {displayedItems.map((n) => (
            <button
              key={n.id}
              onClick={() => handleRowClick(n)}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '11px 14px',
                borderTop: '1px solid var(--border)',
                borderLeft: `3px solid ${n.readAt === null ? rowColor(n) : 'transparent'}`,
                background: n.readAt === null ? 'var(--surface)' : 'var(--page-bg)',
              }}
            >
              <span
                style={{
                  display: 'block',
                  fontSize: 13.5,
                  fontWeight: n.readAt === null ? 600 : 400,
                }}
              >
                {n.title}
              </span>
              {n.body && (
                <span
                  style={{
                    display: 'block',
                    fontSize: 12.5,
                    color: 'var(--text-secondary)',
                  }}
                >
                  {n.body}
                </span>
              )}
              <span
                style={{
                  display: 'block',
                  fontSize: 11.5,
                  color: 'var(--text-secondary)',
                }}
              >
                {formatDateTime(n.createdAt)}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Satır rengi — yeni sunucunun `tier` alanından gelir (S-N sözleşmesi).
 *
 * `tier` yoksa (ESKİ sunucu yanıtı) mevcut `level` dalına düşülür: eski sunucuya karşı
 * davranış DEĞİŞMEZ.
 */
function rowColor(n: Pick<AppNotification, 'tier' | 'level'>): string {
  const tier = n.tier;
  if (tier === 'urgent') return 'var(--danger)';
  if (tier === 'action') return 'var(--warning)';
  if (tier === 'info') return 'var(--primary)';
  return levelColor(n.level);
}

/** ESKİ sunucu yanıtı için `level` → renk (tier yoksa bu dal kullanılır). */
function levelColor(level: string): string {
  if (level === 'CRITICAL') return 'var(--danger)';
  if (level === 'WARNING') return 'var(--warning)';
  return 'var(--primary)';
}
